/**
 * Component: Chat Widget Surface
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R09, WIDGET-V1-R17, GALEN-CHAT-V1-R02,
 *               GALEN-CHAT-V1-R03, GALEN-CHAT-V1-R12
 *
 * Embeddable Chat surface. Wraps the
 * ``POST /api/v1/chat/stream`` SSE endpoint so host pages can drop
 * a clinician-facing Galen Chat into a single ``<div>`` via either
 * the React ``<GalenChat />`` component (NPM path) or the auto-init
 * ``data-galen-surface="chat"`` attribute (UMD path).
 *
 * AB#4401 (2026-05-12): brought to UI parity with the
 * FRONTEND-WEB-V1 Chat page — markdown response rendering
 * (react-markdown + remark-gfm, the same library the portal uses)
 * with ``[docN]`` inline superscript citation refs; a back-and-
 * forth conversation flow (the submitted question becomes a
 * right-aligned user bubble in the thread, not text left in the
 * input); an expandable references panel that mirrors the portal's
 * CitationBlock — hyperlinked ``ShortTitle – GuidelineSectionName``
 * label + snippet, click to expand the full section / recommendation
 * text (SafeHtml/DOMPurify), COR/LOE bands, the cross-module
 * "View in full guideline" BrowseLink at the bottom of each entry;
 * a bottom input row with a round send button; per-message header
 * rows with bot / user avatars and a configurable assistant name
 * (the ``assistant_name`` widget-config field, default "Galen").
 * Multi-turn is wired — the ``conversation_id`` from the stream is
 * threaded back on follow-ups so the backend keeps context.
 *
 * AB#4409 (2026-05-12): portal-style empty state (sparkle glyph,
 * "How can I help you today?", a "generated answers …" sub-line); a
 * persistent clinical-safety disclaimer below the input, sourced
 * from the branding ``safety_disclaimer`` (surfaced on the widget
 * config response as ``chat_safety_disclaimer``) with the same
 * built-in fallback the portal uses; and a send button forced round
 * regardless of host-page ``button`` resets.
 *
 * Still out of scope (separate polish): safety-validator display
 * rules (SAFETY-V1-R05 — the backend now folds safety responses
 * into the normal start/chunk/done text), the conversation-history
 * sidebar, WCAG-AA parity sweep (WIDGET-V1-R21).
 *
 * State machine (per turn):
 *
 *     idle -> submitting -> streaming -> idle (assistant msg 'done')
 *                        \-> idle (assistant msg 'error')
 *
 * Any non-recoverable error routes through the passed ``onError``
 * callback AND renders inline (on the failed assistant message) so a
 * clinician sees a visible failure even when the host has no
 * onError handler.
 *
 * Risk Links: R7, R11
 * FMEA Links: FM7, FM11
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { WidgetApiClient } from '../api/client';
import {
  WidgetAuthError,
  WidgetError,
  WidgetNetworkError,
  WidgetRateLimitError,
} from '../api/errors';
import { streamPostJson, type SseEvent } from '../api/sse';
import { trackWidgetUsage } from '../core/usage-tracking';
import { BrowseLink } from './browse-link';
import { ChatMarkdown } from './chat-markdown';
import { SafeHtml } from './browse-content';

/** Default assistant display name when the widget config omits it. */
const DEFAULT_ASSISTANT_NAME = 'Galen';

/**
 * GALEN-CHAT-V1-R02 / CONFIG-V1-R14: fallback clinical-safety
 * disclaimer rendered below the chat input when the deployment has
 * not configured one. Kept verbatim in sync with the portal's
 * fallback (`src/frontend/src/features/chat/ChatPage.tsx`).
 */
const DEFAULT_SAFETY_DISCLAIMER =
  'Galen Chat retrieves content from published clinical guidelines. It does ' +
  'not analyse patient data, recommend diagnostic or therapeutic actions, or ' +
  'interpret clinical parameters. All clinical decisions remain the sole ' +
  'responsibility of the healthcare professional.';

/**
 * Make the send button a true circle regardless of the host page's
 * ``button`` resets. Inline styles can't carry ``!important``, and a
 * host stylesheet that sets e.g. ``button { width: auto }`` (or adds
 * padding) turns the button into an ellipse. A scoped ``<style>``
 * block — same technique the Semantic Search surface uses for the
 * autofill workaround — wins the cascade. ``aspect-ratio: 1`` plus
 * the fixed width/height keeps it perfectly round even if the host
 * resets one axis.
 */
const CHAT_SEND_BUTTON_CSS = [
  '[data-galen-surface="chat"] [data-galen-chat-submit]{',
  'box-sizing:border-box!important;',
  'width:1.9rem!important;min-width:1.9rem!important;max-width:1.9rem!important;',
  'height:1.9rem!important;min-height:1.9rem!important;max-height:1.9rem!important;',
  'aspect-ratio:1!important;flex:0 0 auto!important;',
  'padding:0!important;border-radius:9999px!important;line-height:1!important;',
  '}',
].join('');

/**
 * Shape of the chat stream's SSE event bodies. The backend emits
 * ``data:``-only SSE frames (no ``event:`` framing line); the event
 * TYPE lives in the JSON body's ``event`` field (AB#4384).
 */
interface ChatStreamEvent {
  event: 'start' | 'chunk' | 'done' | 'error';
  content?: string;
  /** Running total visible-text payload — surface uses this
      directly so a missed chunk doesn't break rendering. */
  cumulative?: string;
  citations?: BackendCitation[];
  is_grounded?: boolean;
  message_id?: string | null;
  conversation_id?: string;
  safety_decision?: 'allow' | 'block' | 'clarify';
  error?: string;
}

/**
 * Backend citation row. Pinned against the dict shape the chat
 * stream's ``done`` event sends (``stream_citations`` in
 * app.api.v1.endpoints.chat — a direct passthrough of the
 * LLMSemanticSearch result fields, PascalCase keys). All fields
 * past ``id`` are optional because upstream results don't always
 * carry every field.
 */
interface BackendCitation {
  id: string;
  GuidelineId?: number | null;
  GuidelineSectionId?: number | null;
  ShortTitle?: string | null;
  GuidelineSectionName?: string | null;
  RecommendationText?: string | null;
  ExplanatoryText?: string | null;
  IndicationText?: string | null;
  RecommendationStrength?: string | null;
  EvidenceStrength?: string | null;
  SectionText?: string | null;
  PageReference?: string | null;
}

/** Widget-internal citation shape consumed by the references panel. */
interface ChatCitation {
  id: string;
  /** 1-based index — matches the ``[docN]`` markers in the text. */
  index: number;
  guidelineName: string | null;
  sectionName: string | null;
  guidelineId: number | null;
  guidelineSectionId: number | null;
  recommendationText: string | null;
  explanatoryText: string | null;
  indicationText: string | null;
  sectionText: string | null;
  cor: string | null;
  loe: string | null;
  pageReference: string | null;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  citations: ChatCitation[];
  isGrounded: boolean;
  status: 'streaming' | 'done' | 'error';
  errorMessage: string | null;
  timestamp: number;
}

export interface GalenChatProps {
  client: WidgetApiClient;
  /** Base URL of the Galen deployment for streaming calls. */
  baseUrl: string;
  /** JWT to send as Bearer on the stream request. */
  token: string;
  onError?: (error: Error) => void;
  /**
   * BROWSE-V1-R20 / AB#4396: when true each citation entry
   * renders a "View in full guideline" link that swaps the widget
   * to the BROWSE-V1 viewer in place (in-place navigation via
   * SurfaceHost). Sourced from widget config (`enable_browse_links`).
   */
  enableBrowseLinks?: boolean;
  /**
   * AB#4401: display name shown for the assistant in each
   * response's header row. Sourced from the `assistant_name`
   * widget-config field; defaults to "Galen".
   */
  assistantName?: string;
  /**
   * GALEN-CHAT-V1-R02: persistent clinical-safety disclaimer
   * rendered below the input. Sourced from the active branding
   * config's `disclaimers.safety_disclaimer`, surfaced on the
   * widget-config response as `chat_safety_disclaimer`. Falls back
   * to the built-in default when the deployment hasn't set one.
   */
  safetyDisclaimer?: string;
}

function mapBackendCitations(rows: BackendCitation[]): ChatCitation[] {
  return rows.map((c, i) => ({
    id: c.id || `cite-${i + 1}`,
    index: i + 1,
    guidelineName: c.ShortTitle ?? null,
    sectionName: c.GuidelineSectionName ?? null,
    guidelineId: c.GuidelineId ?? null,
    guidelineSectionId: c.GuidelineSectionId ?? null,
    recommendationText: c.RecommendationText ?? null,
    explanatoryText: c.ExplanatoryText ?? null,
    indicationText: c.IndicationText ?? null,
    sectionText: c.SectionText ?? null,
    cor: c.RecommendationStrength ?? null,
    loe: c.EvidenceStrength ?? null,
    pageReference: c.PageReference ?? null,
  }));
}

function citationLabel(c: ChatCitation): string {
  const title = c.guidelineName?.trim() || null;
  const section = c.sectionName?.trim() || null;
  if (title && section) return `${title} – ${section}`;
  return title || section || c.id;
}

function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function citationSnippet(c: ChatCitation, max = 140): string {
  const source =
    c.recommendationText || c.sectionText || c.indicationText || '';
  const text = stripTags(source);
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, '') + '…';
}

function genId(): string {
  return 'm' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/* ------------------------------------------------------------------ */
/* Inline icons (generic SVG glyphs — no icon-library dependency).     */
/* ------------------------------------------------------------------ */

const svgProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function BotGlyph(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" {...svgProps} aria-hidden="true">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <path d="M8 16h0M16 16h0" />
    </svg>
  );
}

function UserGlyph(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" {...svgProps} aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function SendGlyph(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" {...svgProps} aria-hidden="true">
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22l-4-9-9-4 20-7z" />
    </svg>
  );
}

/** Lucide `Sparkles` — the empty-state glyph (mirrors the portal). */
function SparkleGlyph(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" {...svgProps} aria-hidden="true">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </svg>
  );
}

/** Lucide `Info` — the leading glyph for the safety disclaimer. */
function InfoGlyph(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" {...svgProps} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

function ChevronGlyph({ open }: { open: boolean }): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      {...svgProps}
      aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function Avatar({ role }: { role: 'user' | 'assistant' }): React.ReactElement {
  const isUser = role === 'user';
  return (
    <span
      data-galen-chat-avatar={role}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1.5rem',
        height: '1.5rem',
        borderRadius: '50%',
        flexShrink: 0,
        background: isUser
          ? 'var(--galen-primary, #2563eb)'
          : 'var(--galen-avatar-bg, #e2e8f0)',
        color: isUser
          ? 'var(--galen-primary-foreground, #ffffff)'
          : 'var(--galen-foreground, #0f172a)',
      }}
    >
      {isUser ? <UserGlyph /> : <BotGlyph />}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* References panel — widget analogue of the portal's CitationBlock.  */
/* ------------------------------------------------------------------ */

const COR_BG: Record<string, string> = {
  I: '#dcfce7',
  IIa: '#fef9c3',
  IIb: '#fed7aa',
  III: '#fee2e2',
};

function CitationEntry({
  citation,
  expanded,
  onToggle,
  client,
  enableBrowseLinks,
}: {
  citation: ChatCitation;
  expanded: boolean;
  onToggle: () => void;
  client: WidgetApiClient;
  enableBrowseLinks: boolean;
}): React.ReactElement {
  const c = citation;
  const isRecommendation = (c.recommendationText ?? '').trim().length > 0;
  return (
    <li
      id={`galen-cite-${c.index}`}
      data-galen-chat-citation-entry={c.index}
      style={{
        listStyle: 'none',
        padding: '0.5rem 0.6rem',
        borderTop: '1px solid var(--galen-border, #e2e8f0)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.4rem',
          width: '100%',
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '1.1rem',
            height: '1.1rem',
            borderRadius: '3px',
            fontSize: '0.7rem',
            fontWeight: 600,
            flexShrink: 0,
            marginTop: '1px',
            color: 'var(--galen-primary, #2563eb)',
            background: 'var(--galen-primary-soft, rgba(37, 99, 235, 0.12))',
          }}
        >
          {c.index}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            data-galen-chat-citation-label
            style={{
              color: 'var(--galen-primary, #2563eb)',
              textDecoration: 'underline',
              fontSize: '0.85rem',
            }}
          >
            {citationLabel(c)}
          </span>
          {!expanded && citationSnippet(c).length > 0 && (
            <span
              style={{
                display: 'block',
                fontSize: '0.78rem',
                color: 'var(--galen-muted, #475569)',
                marginTop: '2px',
              }}
            >
              {citationSnippet(c)}
            </span>
          )}
        </span>
        <ChevronGlyph open={expanded} />
      </button>

      {expanded && (
        <div data-galen-chat-citation-detail style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          {(c.cor || c.loe) && (
            <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.4rem' }}>
              {c.cor && (
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '4px',
                    background: COR_BG[c.cor] ?? 'var(--galen-avatar-bg, #e2e8f0)',
                  }}
                >
                  CoR {c.cor}
                </span>
              )}
              {c.loe && (
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '4px',
                    background: 'var(--galen-avatar-bg, #e2e8f0)',
                  }}
                >
                  LoE {c.loe}
                </span>
              )}
            </div>
          )}
          {isRecommendation ? (
            <>
              {c.recommendationText && (
                <SafeHtml
                  html={c.recommendationText}
                  style={{ fontSize: '0.85rem', lineHeight: 1.45 }}
                />
              )}
              {c.explanatoryText && (
                <SafeHtml
                  html={c.explanatoryText}
                  style={{
                    fontSize: '0.82rem',
                    lineHeight: 1.4,
                    color: 'var(--galen-muted, #475569)',
                    marginTop: '0.35rem',
                  }}
                />
              )}
              {c.indicationText && (
                <SafeHtml
                  html={c.indicationText}
                  style={{
                    fontSize: '0.82rem',
                    lineHeight: 1.4,
                    color: 'var(--galen-muted, #475569)',
                    marginTop: '0.35rem',
                  }}
                />
              )}
            </>
          ) : (
            c.sectionText && (
              <SafeHtml
                html={c.sectionText}
                style={{ fontSize: '0.85rem', lineHeight: 1.45 }}
              />
            )
          )}
          {c.pageReference && (
            <div style={{ fontSize: '0.78rem', color: 'var(--galen-muted, #475569)', marginTop: '0.35rem' }}>
              {/^https?:\/\//i.test(c.pageReference) ? (
                <a href={c.pageReference} target="_blank" rel="noopener noreferrer">
                  {c.pageReference}
                </a>
              ) : (
                c.pageReference
              )}
            </div>
          )}
          {/* BROWSE-V1-R20 / AB#4396: open the cited section in the
              Browse viewer (in-place navigation via SurfaceHost). */}
          <BrowseLink
            enabled={enableBrowseLinks}
            guidelineId={c.guidelineId}
            sectionId={c.guidelineSectionId}
            client={client}
            surfaceKey="chat"
            documentId={c.id}
            rankingPosition={c.index}
            guidelineName={c.guidelineName}
            sectionName={c.sectionName}
          />
        </div>
      )}
    </li>
  );
}

function CitationsPanel({
  citations,
  isGrounded,
  client,
  enableBrowseLinks,
}: {
  citations: ChatCitation[];
  isGrounded: boolean;
  client: WidgetApiClient;
  enableBrowseLinks: boolean;
}): React.ReactElement | null {
  const [panelOpen, setPanelOpen] = useState(true);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  if (citations.length === 0) return null;

  const headerLabel = isGrounded
    ? `${citations.length} reference${citations.length === 1 ? '' : 's'}`
    : 'Related search results';

  const toggleEntry = (index: number): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div
      data-galen-chat-citations
      style={{
        marginTop: '0.6rem',
        border: '1px solid var(--galen-border, #e2e8f0)',
        borderRadius: 'var(--galen-border-radius, 0.5rem)',
        background: 'var(--galen-background, #ffffff)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        data-galen-chat-citations-toggle
        onClick={() => setPanelOpen((o) => !o)}
        aria-expanded={panelOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          width: '100%',
          border: 'none',
          background: 'var(--galen-muted-bg, #f8fafc)',
          padding: '0.45rem 0.6rem',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: '0.82rem',
          fontWeight: 600,
          color: 'var(--galen-foreground, #0f172a)',
        }}
      >
        <span>{headerLabel}</span>
        <ChevronGlyph open={panelOpen} />
      </button>
      {panelOpen && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {citations.map((c) => (
            <CitationEntry
              key={c.id}
              citation={c}
              expanded={expanded.has(c.index)}
              onToggle={() => toggleEntry(c.index)}
              client={client}
              enableBrowseLinks={enableBrowseLinks}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Message rows.                                                       */
/* ------------------------------------------------------------------ */

function UserMessageRow({ message }: { message: ChatMessage }): React.ReactElement {
  return (
    <div
      data-galen-chat-message
      data-galen-chat-role="user"
      style={{
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'flex-start',
        gap: '0.5rem',
      }}
    >
      <Avatar role="user" />
      <div style={{ maxWidth: '80%' }}>
        <div
          style={{
            background: 'var(--galen-primary, #2563eb)',
            color: 'var(--galen-primary-foreground, #ffffff)',
            padding: '0.5rem 0.75rem',
            borderRadius: '1rem',
            borderTopRightRadius: '0.25rem',
            fontSize: '0.95rem',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.text}
        </div>
        <div
          style={{
            fontSize: '0.7rem',
            color: 'var(--galen-muted, #475569)',
            textAlign: 'right',
            marginTop: '2px',
          }}
        >
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
}

function AssistantMessageRow({
  message,
  assistantName,
  client,
  enableBrowseLinks,
}: {
  message: ChatMessage;
  assistantName: string;
  client: WidgetApiClient;
  enableBrowseLinks: boolean;
}): React.ReactElement {
  const streaming = message.status === 'streaming';
  return (
    <div
      data-galen-chat-message
      data-galen-chat-role="assistant"
      style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Avatar role="assistant" />
        <span data-galen-chat-assistant-name style={{ fontSize: '0.85rem', fontWeight: 500 }}>
          {assistantName}
        </span>
        <span style={{ fontSize: '0.7rem', color: 'var(--galen-muted, #475569)' }}>
          {formatTime(message.timestamp)}
        </span>
      </div>
      <div style={{ paddingLeft: '1.9rem' }}>
        {message.status === 'error' ? (
          <div
            data-galen-chat-error
            role="alert"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--galen-error-border, #fca5a5)',
              background: 'var(--galen-error-bg, #fef2f2)',
              color: 'var(--galen-error-fg, #991b1b)',
              borderRadius: 'var(--galen-border-radius, 0.5rem)',
              fontSize: '0.9rem',
            }}
          >
            {message.errorMessage ?? 'Something went wrong — please try again.'}
          </div>
        ) : (
          <>
            {message.text.length > 0 && (
              <ChatMarkdown content={message.text} citationCount={message.citations.length} />
            )}
            {streaming && (
              <span aria-hidden="true" style={{ opacity: 0.5, marginLeft: message.text.length > 0 ? '0.2rem' : 0 }}>
                {message.text.length === 0 ? 'Thinking…' : '▋'}
              </span>
            )}
            {!streaming && (
              <CitationsPanel
                citations={message.citations}
                isGrounded={message.isGrounded}
                client={client}
                enableBrowseLinks={enableBrowseLinks}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Surface.                                                            */
/* ------------------------------------------------------------------ */

type ChatPhase = 'idle' | 'submitting' | 'streaming';

export const GalenChat: React.FC<GalenChatProps> = ({
  client,
  baseUrl,
  token,
  onError,
  enableBrowseLinks = false,
  assistantName,
  safetyDisclaimer,
}) => {
  const name = (assistantName ?? '').trim() || DEFAULT_ASSISTANT_NAME;
  const disclaimerText =
    (safetyDisclaimer ?? '').trim() || DEFAULT_SAFETY_DISCLAIMER;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState('');
  const [phase, setPhase] = useState<ChatPhase>('idle');
  const conversationIdRef = useRef<string | null>(null);
  const assistantMsgIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const busy = phase !== 'idle';

  useEffect(() => {
    const end = threadEndRef.current;
    // `scrollIntoView` is not implemented in jsdom (and a no-op
    // pre-layout); guard so the effect never throws there.
    if (end != null && typeof end.scrollIntoView === 'function') {
      end.scrollIntoView({ block: 'end' });
    }
  }, [messages]);

  const patchAssistant = useCallback((patch: Partial<ChatMessage>) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === assistantMsgIdRef.current ? { ...m, ...patch } : m)),
    );
  }, []);

  const newChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    conversationIdRef.current = null;
    assistantMsgIdRef.current = null;
    setMessages([]);
    setPrompt('');
    setPhase('idle');
  }, []);

  const submit = useCallback(async () => {
    if (busy) return;
    const query = prompt.trim();
    if (query.length === 0) return;

    const now = Date.now();
    const userMsg: ChatMessage = {
      id: genId(),
      role: 'user',
      text: query,
      citations: [],
      isGrounded: false,
      status: 'done',
      errorMessage: null,
      timestamp: now,
    };
    const asstId = genId();
    const asstMsg: ChatMessage = {
      id: asstId,
      role: 'assistant',
      text: '',
      citations: [],
      isGrounded: false,
      status: 'streaming',
      errorMessage: null,
      timestamp: now,
    };
    assistantMsgIdRef.current = asstId;
    setMessages((prev) => [...prev, userMsg, asstMsg]);
    setPrompt('');
    setPhase('submitting');

    // WIDGET-V1-R25: COUNTER chat_query event (fire-and-forget).
    void trackWidgetUsage(client, 'chat', 'chat_query', { query_text: query });

    const finishStreaming = (): void => {
      setPhase('idle');
      abortRef.current = null;
    };

    try {
      const controller = await streamPostJson(
        { baseUrl, token },
        '/api/v1/chat/stream',
        {
          query,
          ...(conversationIdRef.current
            ? { conversation_id: conversationIdRef.current }
            : {}),
        },
        {
          onToken: (evt: SseEvent) => {
            let body: ChatStreamEvent;
            try {
              body = JSON.parse(evt.data) as ChatStreamEvent;
            } catch {
              return;
            }
            switch (body.event) {
              case 'start':
                if (typeof body.conversation_id === 'string') {
                  conversationIdRef.current = body.conversation_id;
                }
                setPhase('streaming');
                return;
              case 'chunk':
                if (typeof body.cumulative === 'string') {
                  patchAssistant({ text: body.cumulative });
                } else if (typeof body.content === 'string') {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMsgIdRef.current
                        ? { ...m, text: m.text + (body.content ?? '') }
                        : m,
                    ),
                  );
                }
                setPhase('streaming');
                return;
              case 'done': {
                if (typeof body.conversation_id === 'string') {
                  conversationIdRef.current = body.conversation_id;
                }
                const patch: Partial<ChatMessage> = {
                  status: 'done',
                  isGrounded: body.is_grounded === true,
                };
                if (typeof body.cumulative === 'string') patch.text = body.cumulative;
                if (Array.isArray(body.citations)) {
                  patch.citations = mapBackendCitations(body.citations);
                }
                patchAssistant(patch);
                return;
              }
              case 'error': {
                const transportErr = new WidgetNetworkError(
                  body.error ?? 'Chat stream reported a transport error.',
                  null,
                );
                patchAssistant({ status: 'error', errorMessage: explainError(transportErr) });
                onError?.(transportErr);
                return;
              }
              default:
                return;
            }
          },
          onDone: () => {
            // Flip a still-streaming message to done if the
            // connection closed without an explicit done event.
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgIdRef.current && m.status === 'streaming'
                  ? { ...m, status: 'done' }
                  : m,
              ),
            );
            finishStreaming();
          },
          onError: (err) => {
            patchAssistant({ status: 'error', errorMessage: explainError(err) });
            onError?.(err);
            finishStreaming();
          },
        },
      );
      abortRef.current = controller;
    } catch (e) {
      const err = e instanceof Error ? e : new WidgetError(String(e));
      patchAssistant({ status: 'error', errorMessage: explainError(err) });
      onError?.(err);
      finishStreaming();
    }
  }, [busy, prompt, baseUrl, token, client, onError, patchAssistant]);

  const onFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      void submit();
    },
    [submit],
  );

  const onTextareaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void submit();
      }
    },
    [submit],
  );

  return (
    <div
      data-galen-surface="chat"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6rem',
        fontFamily: 'var(--galen-font-family, system-ui, sans-serif)',
        color: 'var(--galen-foreground, #0f172a)',
      }}
    >
      <style>{CHAT_SEND_BUTTON_CSS}</style>
      {messages.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-galen-chat-new
            onClick={newChat}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '0.78rem',
              color: 'var(--galen-primary, #2563eb)',
              textDecoration: 'underline',
              padding: '0.1rem 0.25rem',
            }}
          >
            New chat
          </button>
        </div>
      )}

      <div
        data-galen-chat-thread
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.85rem',
          maxHeight: '60vh',
          overflowY: 'auto',
          padding: messages.length > 0 ? '0.25rem 0.1rem' : 0,
        }}
      >
        {messages.length === 0 && (
          <div
            data-galen-chat-empty
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: '0.5rem',
              padding: '2.5rem 1rem',
              minHeight: '40vh',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '3.5rem',
                height: '3.5rem',
                borderRadius: '0.9rem',
                background: 'var(--galen-primary-soft, rgba(37, 99, 235, 0.1))',
                color: 'var(--galen-primary, #2563eb)',
                marginBottom: '0.5rem',
              }}
            >
              <SparkleGlyph />
            </span>
            {/* A <p>, not an <h*> — an embeddable widget shouldn't
                assume its host page's heading hierarchy (an isolated
                <h2> trips axe's page-has-heading-one). */}
            <p
              data-galen-chat-empty-heading
              style={{
                margin: 0,
                fontSize: '1.15rem',
                fontWeight: 600,
                color: 'var(--galen-foreground, #0f172a)',
              }}
            >
              How can I help you today?
            </p>
            <p
              style={{
                margin: 0,
                maxWidth: '28rem',
                fontSize: '0.9rem',
                lineHeight: 1.5,
                color: 'var(--galen-muted, #475569)',
              }}
            >
              Ask questions about clinical guidelines and receive generated
              answers with citations to relevant guideline sections.
            </p>
          </div>
        )}
        {messages.map((m) =>
          m.role === 'user' ? (
            <UserMessageRow key={m.id} message={m} />
          ) : (
            <AssistantMessageRow
              key={m.id}
              message={m}
              assistantName={name}
              client={client}
              enableBrowseLinks={enableBrowseLinks}
            />
          ),
        )}
        <div ref={threadEndRef} />
      </div>

      <form data-galen-chat-form onSubmit={onFormSubmit}>
        <div
          style={{
            position: 'relative',
            border: '1px solid var(--galen-border, #e2e8f0)',
            borderRadius: 'var(--galen-border-radius, 0.5rem)',
            background: 'var(--galen-background, #ffffff)',
          }}
        >
          <label htmlFor="galen-chat-prompt" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Ask {name}
          </label>
          <textarea
            id="galen-chat-prompt"
            data-galen-chat-prompt
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onTextareaKeyDown}
            rows={2}
            disabled={busy}
            placeholder={`Ask ${name} a question about clinical guidelines…`}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '0.55rem 2.6rem 0.55rem 0.65rem',
              border: 'none',
              borderRadius: 'var(--galen-border-radius, 0.5rem)',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              resize: 'vertical',
              background: 'transparent',
              color: 'inherit',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            data-galen-chat-submit
            disabled={busy || prompt.trim().length === 0}
            aria-label={`Send message to ${name}`}
            style={{
              position: 'absolute',
              right: '0.4rem',
              bottom: '0.4rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              width: '1.9rem',
              height: '1.9rem',
              flexShrink: 0,
              padding: 0,
              border: 'none',
              borderRadius: '50%',
              background: 'var(--galen-primary, #2563eb)',
              color: 'var(--galen-primary-foreground, #ffffff)',
              cursor: busy || prompt.trim().length === 0 ? 'not-allowed' : 'pointer',
              opacity: busy || prompt.trim().length === 0 ? 0.5 : 1,
            }}
          >
            <SendGlyph />
          </button>
        </div>
      </form>

      {/* GALEN-CHAT-V1-R02: persistent clinical-safety disclaimer. */}
      <div
        data-galen-chat-disclaimer
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.35rem',
          fontSize: '0.7rem',
          lineHeight: 1.45,
          color: 'var(--galen-muted, #475569)',
        }}
      >
        <span style={{ flexShrink: 0, marginTop: '0.15rem' }}>
          <InfoGlyph />
        </span>
        <p style={{ margin: 0 }}>{disclaimerText}</p>
      </div>
    </div>
  );
};

/**
 * Map a typed widget error into the short, user-visible message a
 * failed assistant turn renders. Long-form guidance lives in the
 * shared R20 error card; this gives a terse inline version.
 */
function explainError(error: Error): string {
  if (error instanceof WidgetRateLimitError) {
    if (error.retryAfterSeconds !== null) {
      return `Rate limit reached — please try again in about ${error.retryAfterSeconds} seconds.`;
    }
    return 'Rate limit reached — please try again in a few minutes.';
  }
  if (error instanceof WidgetAuthError) {
    return 'Authentication expired — please refresh the page.';
  }
  if (error instanceof WidgetNetworkError) {
    return 'Galen is temporarily unreachable — please check your network and try again.';
  }
  return error.message || 'Something went wrong — please try again.';
}
